<template>
  <div class="hr-auto-complete" :class="{ active: state.active }">
    <hr-input
      ref="input"
      :value="state.filter"
      class="hr-auto-complete__input"
      v-bind="$attrs"
      :disabled="disabled"
      @update="query"
      @keyup="keyUp"
      @blur="blur"
      @focus="focus"
      @click="click"
    >
      <template v-slot:icon>
        <div
          class="hr-auto-complete__input__icon"
          :class="{ state: $attrs.invalid !== null }"
          @click="clearFilter"
        >
          <co-icon
            v-if="!state.filter && !$slots.icon && !disabled"
            class="hr-auto-complete__search-icon"
            name="search"
          ></co-icon>

          <template v-if="!state.filter && $slots.icon">
            <slot name="icon"></slot>
          </template>

          <span v-if="state.filter && !disabled">
            {{ t('hr-auto-complete:clear') }}</span
          >
        </div>
      </template>
    </hr-input>

    <div
      v-if="state.isListVisible"
      class="hr-auto-complete__helper"
      @click.prevent="selectAddItem()"
    >
      <slot name="helper"></slot>
    </div>

    <div
      v-if="state.isListVisible"
      ref="list"
      class="hr-auto-complete__list"
      @click="clickList"
    >
      <div
        v-for="(item, index) in state.theLocalFiltered"
        :key="index"
        ref="listItem"
        :tabindex="index"
        class="hr-auto-complete__list__item"
        @click="selectItem(item)"
        @keyup.enter="selectItem(item)"
        @keyup.exact="navigate($event, index)"
      >
        {{ description ? item[description] : item.description }}
      </div>
    </div>
    <div v-if="$slots.invalid && !disabled" class="hr-auto-complete__invalid">
      <slot name="invalid"></slot>
    </div>
  </div>
</template>

<script>
import { reactive, watch, ref } from 'vue';
import HrInput from '@/components/hr-input.vue';
import { CoIcon } from '@deputy/copilot2';
import t from '@/lib/i18next';
import store from '@/store';
import { logWebHrError } from '@/core/logger/log-wrapper';
export default {
  name: 'hr-auto-complete',
  components: { HrInput, CoIcon },
  props: {
    external: {
      type: Function,
      default: null,
      description: `should be an async function using promises or async and return an array of items`,
    },
    local: {
      type: Array,
      default: () => [],
      description: `provide an array of items to be used pattern [{id, description}]`,
    },
    debounce: {
      type: Number,
      default: 300,
      description: `debounce time to be used when external source is set`,
    },
    value: {
      type: String,
      default: '',
      description: `default value`,
    },
    id: {
      type: String,
      default: null,
      description: `if the name of the "id" key in the object array is different then "id" use this`,
    },
    description: {
      type: String,
      default: null,
      description: `if the name of the "description" key in the object array is different then "description" use this`,
    },
    beforeInput: {
      type: Function,
      default: null,
      description: `if function exists execute before emit the input event`,
    },
    add: {
      type: String,
      default: null,
      description: `add new item string if set will display this option in the list`,
    },
    disabled: {
      type: Boolean,
      default: null,
    },
  },
  emits: [
    'input',
    'blur',
    'focus',
    'click',
    'keyup',
    'update',
    'showAddressFields',
  ],
  setup(props, { emit }) {
    const state = reactive({
      theValue: props.value,
      theLocal: [],
      theLocalFiltered: [],
      active: false,
      loading: false,
      filter: props.value,
      isListVisible: false,
      typed: '',
      keyup: {},
      keyupVal: '',
      input: {},
      inputVal: '',
      closeList: () => {
        state.isListVisible = false;
      },
    });
    const listItem = ref(null);
    const input = ref(null);

    watch(
      () => state.isListVisible,
      (newValue) => {
        if (true === newValue) {
          document.addEventListener('click', state.closeList);
        } else {
          document.removeEventListener('click', state.closeList);
        }
      }
    );

    function initialize() {
      if (props.local.length > 0) {
        state.theLocalFiltered = state.theLocal = props.local;
      }
    }
    initialize();

    function clickList(e) {
      e.stopPropagation();
    }

    function navigate(evt, index) {
      evt.preventDefault();
      evt.stopPropagation();

      if (evt.code === 'ArrowDown') {
        evt.preventDefault();
        evt.stopPropagation();
        const jumpTo = index + 1;

        if (jumpTo >= listItem.value.length) {
          return true;
        }

        listItem.value[jumpTo].focus();
      } else if (evt.code === 'ArrowUp') {
        evt.preventDefault();
        evt.stopPropagation();
        const jumpTo = index - 1;

        if (jumpTo < 0) {
          return true;
        }

        listItem.value[jumpTo].focus();
      }
    }

    function blur() {
      emit('blur', state.filter);
    }

    function focus() {
      emit('focus');
    }

    function click() {
      emit('click');
    }

    function keyUp(evt, val) {
      state.keyup = {
        type: evt.type,
        key: evt.key,
        keyCode: evt.keyCode,
        code: evt.code,
      };
      state.keyupVal = val;

      state.typed = state.filter;
      emit('keyup', state.theValue);

      if (!state.active) {
        return true;
      }

      if (evt.code === 'ArrowDown') {
        listItem.value[0].focus();
      }
    }

    function selectAddItem() {
      if (props.add) {
        state.isListVisible = false;
        state.active = false;

        emit('update', state.filter, {});
        emit('showAddressFields');
      }
    }

    async function selectItem(item) {
      state.isListVisible = false;
      state.active = false;

      let returnItem = item;

      if (props.id) {
        returnItem[props.id] = item[props.id];
      }

      if (props.description) {
        returnItem[props.description] = item[props.description];
        state.theValue = item[props.description];
        state.filter = item[props.description];
      } else {
        state.theValue = item.description;
        state.filter = item.description;
      }

      if (props.beforeInput) {
        try {
          return emit(
            'update',
            state.filter,
            await props.beforeInput(returnItem)
          );
        } catch (e) {
          logWebHrError({
            message: 'beforeInput error when selecting item',
            source: 'hr-auto-complete',
            error: e,
          });
        }
      }

      emit('update', returnItem, {});
    }

    // TODO: add a debounce to the search
    async function queryExternal(filter) {
      try {
        state.theLocalFiltered = await props.external(filter);
        state.active = true;
        state.loading = false;
      } catch (e) {
        logWebHrError({
          message: 'Failed to query external error',
          source: 'hr-auto-complete:queryExternal',
          error: e,
        });
      }
    }

    function query(filter, obj, evt) {
      if (evt) {
        state.input = {
          type: evt.type,
          key: evt.key,
          keyCode: evt.keyCode,
          code: evt.code,
        };
      }
      state.inputVal = obj;

      state.isListVisible = true;
      state.loading = true;

      if (props.external) {
        return queryExternal(filter);
      }

      state.active = true;

      state.theLocalFiltered = state.theLocal.filter((item) => {
        const itemDescription = props.description
          ? item[props.description]
          : item.description;
        return (
          (itemDescription || '')
            .toLowerCase()
            .indexOf((filter || '').toLowerCase()) > -1
        );
      });

      state.loading = false;
    }

    function clearFilter() {
      state.theValue = null;
      state.filter = null;
      state.isListVisible = false;
      input.value.focus();
      emit('update', '', {});
      store.commit('setSavedSuper', {});
    }

    return {
      t,
      state,
      listItem,
      input,
      clickList,
      navigate,
      blur,
      focus,
      click,
      keyUp,
      selectAddItem,
      selectItem,
      query,
      clearFilter,
    };
  },
};
</script>

<style>
.hr-auto-complete__list {
  display: none;
  border: 1px solid black;
  width: 100%;
  max-height: 108px;
  overflow: auto;
}

.hr-auto-complete__input__icon span {
  text-transform: uppercase;
  padding: 18px 12px;
  font-size: 0.8em;
}

.hr-auto-complete__input__icon {
  cursor: pointer;
}

.hr-auto-complete__list__item:focus {
  background: #ddd;
}

.hr-auto-complete__list__item:hover {
  background: #efefef;
}

.hr-auto-complete__list__item {
  cursor: pointer;
  padding: 5px;
  margin: 1px 0;
}

.hr-auto-complete.active .hr-auto-complete__list {
  display: block;
}

.hr-auto-complete.active .hr-auto-complete__input .DSInput__input__input {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.hr-auto-complete__input__icon .hr-auto-complete__search-icon {
  width: 20px !important;
  height: 20px !important;
}

.DSInput__valid-icon .co-icon,
.DSInput__invalid-icon .co-icon {
  width: 20px !important;
  height: 20px !important;
  margin-top: 15px !important;
}

.hr-auto-complete__input input {
  border-radius: 8px;
}
.hr-auto-complete__helper {
  cursor: pointer;
}

.hr-auto-complete__invalid {
  color: var(--co-text-attention);
  margin-top: 5px;
}
</style>
